import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import { RouteComponentProps } from 'react-router-dom';
import { AdminFullscreenModalName, ICreateCampaignRequest, ILocationState } from '../../../../models';
import { IEditCampaignRequest } from '../../../../models/AdminModels';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	CreateGhostCalendarSuggestionViewModel,
	QuickSightDashboardViewModel,
} from '../../../../viewmodels/AdminViewModels';
import { PrivateRoute } from '../../../../web/components/PrivateRoute';
import { FullscreenModal } from '../../../../web/components/fullscreen/FullscreenModal';
import { EditHtmlNewsletterTemplate } from '../../../../web/containers/EditHtmlNewsletterTemplate';
import { EmailCampaignHistoricalReport } from '../../../../web/containers/bulkEmailHistorical/EmailCampaignHistoricalReport';
import { AIAssistantStreamingMutationProvider } from '../../../../web/containers/templates/AssistantChat';
import { CopyMageAiChatPage } from '../../../containers/CopyMageAiChat';
import { CopyMageConversationStarterPage } from '../../../containers/CopyMageAiChat/CopyMageConversationStarter';
import { CopyMageLatestConversationPage } from '../../../containers/CopyMageAiChat/CopyMageLatestConversationPage';
import { CopyMageWireframe } from '../../../containers/CopyMageWireframe';
import { Hangfire } from '../../../containers/DevTools/Hangfire';
import { EditAccountQueuedCampaign } from '../../../containers/EditAccountQueuedCampaign';
import { AccountMultiCampaignSchedulerRoutes } from '../../../containers/account/AccountMultiCampaignScheduler';
import { AccountMultiEmailScheduler } from '../../../containers/account/AccountMultiCampaignScheduler/AccountMultiEmailScheduler';
import { AccountContentCalendarBlogRoute } from '../../../containers/blogs/AccountContentCalendarBlogRoute';
import { CreateBulkEmailApproval } from '../../../containers/bulkEmailApproval/CreateBulkEmailApproval';
import { CreateContentCalendarCampaign } from '../../../containers/bulkEmailApproval/CreateContentCalendarCampaign';
import { AdminEditSocialMediaPost } from '../../../containers/socialMedia/AdminEditSocialMediaPost';
import { AdminPreviewSocialMediaPost } from '../../../containers/socialMedia/AdminPreviewSocialMediaPost';
import { CreateIndustryAutomationTemplate } from '../../../containers/templates/CreateIndustryAutomationTemplate';
import { EditGhostCalendarSuggestion } from '../../../containers/templates/EditGhostCalendarSuggestion';
import { EditIndustryAutomationTemplate } from '../../../containers/templates/EditIndustryAutomationTemplate';
import { GhostCalendarSendOptions } from '../../../containers/templates/GhostCalendarSendOptions';
import { AdminPrivateRoute } from '../../AdminPrivateRoute';
import { QuickSightDashboard } from '../../QuickSightDashboard';
import { EditCampaignTemplate } from '../../campaigns/EditCampaignTemplate';
import { styleSheet } from './styles';

const loggingCategory = 'AdminFullscreenModal';

function _AdminFullscreenModal() {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const onRenderEditQueuedCampaign = (props: RouteComponentProps<any>) => {
		const locationState: ILocationState<any, IEditCampaignRequest> = fullscreenModal?.history?.location?.state ?? {};

		return <EditAccountQueuedCampaign {...props} editRequest={locationState?.model} />;
	};
	const onRenderContentCalendarCampaignEditor = (props: RouteComponentProps<any>) => {
		const locationState: ILocationState<any, ICreateCampaignRequest> = fullscreenModal?.history?.location?.state ?? {};
		return (
			<AIAssistantStreamingMutationProvider>
				<CreateContentCalendarCampaign {...props} createRequest={locationState?.model} />
			</AIAssistantStreamingMutationProvider>
		);
	};
	const onRenderHtmlNewsletterEditor = (props: RouteComponentProps<any>) => {
		return <EditHtmlNewsletterTemplate {...props} routeContainerClassName='htmlnewsletter' />;
	};
	const onRenderQuicksightDashboard = (props: RouteComponentProps<any>) => {
		const locationState: ILocationState<QuickSightDashboardViewModel, any> =
			fullscreenModal?.history?.location?.state ?? {};
		return <QuickSightDashboard {...props} vm={locationState?.viewModel} />;
	};
	const onRenderCreateIndustryAutomationTemplate = (props: RouteComponentProps<any>) => {
		return <CreateIndustryAutomationTemplate routeContainerClassName={css(styleSheet.defaultChildStyle)} {...props} />;
	};
	const onRenderEditIndustryAutomationTemplate = (props: RouteComponentProps<any>) => {
		return <EditIndustryAutomationTemplate routeContainerClassName={css(styleSheet.defaultChildStyle)} {...props} />;
	};
	const onRenderCreateApproval = (props: RouteComponentProps<any>) => {
		return <CreateBulkEmailApproval {...props} />;
	};
	const onRenderGhostCalendarSuggestionSendOptions = (props: RouteComponentProps<any>) => {
		const locationState: ILocationState<CreateGhostCalendarSuggestionViewModel, any> =
			fullscreenModal?.history?.location?.state ?? {};
		return (
			<div className={css(styleSheet.defaultChildStyle)}>
				<GhostCalendarSendOptions {...props} createSuggestion={locationState.viewModel} />
			</div>
		);
	};
	return (
		<>
			<FullscreenModal loggingCategory={loggingCategory} name={AdminFullscreenModalName}>
				<PrivateRoute path='/copymage/:accountId/conversation/:conversationId' userSession={userSession}>
					<CopyMageAiChatPage />
				</PrivateRoute>
				<PrivateRoute path='/copymage/:accountId/latest-conversation' userSession={userSession}>
					<CopyMageLatestConversationPage />
				</PrivateRoute>
				<PrivateRoute path='/copymage/:accountId/conversation' userSession={userSession}>
					<CopyMageConversationStarterPage />
				</PrivateRoute>
				<PrivateRoute path='/copymage-wireframes/:accountId' userSession={userSession}>
					<CopyMageWireframe />
				</PrivateRoute>
				<PrivateRoute
					path='/email/campaigns/edit'
					exact={false}
					render={onRenderEditQueuedCampaign}
					userSession={userSession}
				/>
				<PrivateRoute path='/email/bulk-email-approval' render={onRenderCreateApproval} userSession={userSession} />
				<AdminPrivateRoute path='/campaign-templates' userSession={userSession}>
					<EditCampaignTemplate />
				</AdminPrivateRoute>
				<PrivateRoute
					exact={true}
					path='/automations/new'
					render={onRenderCreateIndustryAutomationTemplate}
					userSession={userSession}
				/>
				<PrivateRoute
					path='/automations/:id'
					render={onRenderEditIndustryAutomationTemplate}
					userSession={userSession}
				/>
				<PrivateRoute
					path='/accounts/:accountId/content-calendar/multi-campaign-scheduler/email'
					userSession={userSession}
				>
					<AccountMultiEmailScheduler />
				</PrivateRoute>
				<PrivateRoute path='/accounts/:accountId/content-calendar/multi-campaign-scheduler' userSession={userSession}>
					<AccountMultiCampaignSchedulerRoutes />
				</PrivateRoute>
				<PrivateRoute
					path={[
						'/accounts/:accountId/content-calendar/post/create',
						'/accounts/:accountId/content-calendar/post/edit-from-post-report',
						'/accounts/:accountId/content-calendar/post/suggestion/preview-edit/:templateId/:suggestionId',
					]}
					userSession={userSession}
				>
					<AIAssistantStreamingMutationProvider>
						<AdminEditSocialMediaPost />
					</AIAssistantStreamingMutationProvider>
				</PrivateRoute>
				<PrivateRoute
					path='/accounts/:accountId/content-calendar/post-approval/approval-preview'
					userSession={userSession}
				>
					<AdminPreviewSocialMediaPost routeContainerClassName={css(styleSheet.defaultChildStyle)} />
				</PrivateRoute>
				<PrivateRoute
					path='/accounts/:accountId/content-calendar/campaigns/create'
					render={onRenderContentCalendarCampaignEditor}
					userSession={userSession}
				/>
				<PrivateRoute path='/accounts/:accountId/content-calendar/blog' userSession={userSession}>
					<AIAssistantStreamingMutationProvider>
						<AccountContentCalendarBlogRoute />
					</AIAssistantStreamingMutationProvider>
				</PrivateRoute>
				<PrivateRoute
					path='/accounts/:accountId/htmlnewsletter/:id'
					render={onRenderHtmlNewsletterEditor}
					userSession={userSession}
				/>
				<PrivateRoute path='/email/campaign-bulk-historical-report/:accountId/:campaignId' userSession={userSession}>
					<EmailCampaignHistoricalReport />
				</PrivateRoute>

				<PrivateRoute path='/quicksight/dashboard' render={onRenderQuicksightDashboard} userSession={userSession} />
				<PrivateRoute path='/devtools/hangfire' userSession={userSession}>
					<Hangfire />
				</PrivateRoute>
				<PrivateRoute
					exact={true}
					path='/ghost-calendar/suggestion/edit/send-options'
					render={onRenderGhostCalendarSuggestionSendOptions}
					userSession={userSession}
				/>
				<PrivateRoute path='/ghost-calendar/suggestion/edit/:step?' userSession={userSession}>
					<EditGhostCalendarSuggestion routeContainerClassName={css(styleSheet.defaultChildStyle)} />
				</PrivateRoute>
			</FullscreenModal>
		</>
	);
}

export const AdminFullscreenModal = observer(_AdminFullscreenModal);
